import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { IconButton } from '@mui/material';
import { FileDownloadOutlined } from '@mui/icons-material';
import GenericTableWrapper from 'components/UI/GenericTable/GenericTableWrapper';
import { TableColumn } from 'components/UI/GenericTable/GenericTable';
import { AffiliateCommissionData, PayoutRecord } from 'data/AffiliateMember';
import { AFFILIATE_DOCUMENT_CATEGORIES, COMMISSION_TRANSACTION_TYPES, PAYMENT_HISTORY_COLUMNS } from 'constants/affiliate';
import { COUNTRY_ISO } from 'constants/country';
import { downloadDocument, loadPayoutDocuments } from 'requests/documents';
import { renderToast, toast } from 'components/UI/ToastNotification';
import { currencySign } from 'utils/formatters';
import { useAuth } from 'lib/Auth';
import { Text } from 'styles/themeColors';
import { FontSize } from 'lib/styles';
import { sortByDateDesc } from 'utils/sort';

const SectionTitle = styled.h3`
  font-size: ${FontSize.large};
  font-weight: bold;
  color: ${Text.primary};
  margin: 24px 0 16px 0;
`;

// ─── Component ───────────────────────────────────────────────────────────────

interface Props {
  affiliates: AffiliateCommissionData[] | null;
  country?: string;
  sort?: (a: PayoutRecord, b: PayoutRecord) => number;
}

const PaymentHistory = ({ affiliates, country = COUNTRY_ISO.US, sort = sortByDateDesc<PayoutRecord>('paidDate') }: Props) => {
  const { token } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [downloading, setDownloading] = useState<{ id: number; category: string } | null>(null);

  const payoutRecords = useMemo<PayoutRecord[]>(() => {
    const seen = new Set<number>();
    return (affiliates ?? [])
      .flatMap(aff =>
        aff.transactions
          .filter(tx => tx.type === COMMISSION_TRANSACTION_TYPES.PAYOUT && tx.transactionId && tx.paidDate)
          .map(tx => ({
            id: tx.id,
            transactionId: tx.transactionId!,
            paidDate: tx.paidDate ?? '',
            commissionAmount: tx.commissionAmount,
            affiliateCode: aff.affiliateCode,
          }))
      )
      .filter(r => { if (seen.has(r.id)) return false; seen.add(r.id); return true; })
      .sort(sort);
  }, [affiliates, sort]);

  // Reset to first page when data source or page size changes
  useEffect(() => {
    setCurrentPage(1);
  }, [affiliates, itemsPerPage]);

  const totalPages = Math.max(1, Math.ceil(payoutRecords.length / itemsPerPage));
  const visibleRecords = payoutRecords.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handleDownload = useCallback(async (record: PayoutRecord, category: 'pickslip' | 'documents') => {
    setDownloading({ id: record.id, category });
    try {
      const docs = await loadPayoutDocuments([record.affiliateCode, record.transactionId], { token });
      const categoryName = category === 'pickslip'
        ? AFFILIATE_DOCUMENT_CATEGORIES.PICKSLIP
        : AFFILIATE_DOCUMENT_CATEGORIES.RECEIPT;
      const docList: any[] = ((docs as any)?.vendorFiles ?? [])
        .flatMap((vf: any) => vf.documents ?? []);
      const doc = docList.find((d: any) => d.category?.name === categoryName);
      if (!doc) {
        renderToast(toast.TYPE.ERROR, 'Document not found');
        return;
      }
      const result = await downloadDocument([doc.id, doc.url], { token });
      const a = document.createElement('a');
      a.style.display = 'none';
      document.body.appendChild(a);
      const url = window.URL.createObjectURL(result.document);
      a.href = url;
      a.download = `${categoryName}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      renderToast(toast.TYPE.ERROR, 'Download failed');
    } finally {
      setDownloading(null);
    }
  }, [token]);

  const columns: TableColumn[] = useMemo(() => {
    // Scale Payout ID column relative to Date Paid (~20 chars), clamped to [1.5, 4]
    const maxIdLength = payoutRecords.reduce((max, r) => Math.max(max, r.transactionId.length), 0);
    const idFlexGrow = Math.min(Math.max(maxIdLength / 20, 1.5), 4);

    return [
      { ...PAYMENT_HISTORY_COLUMNS[0], headerStyle: { flexGrow: idFlexGrow } },
      PAYMENT_HISTORY_COLUMNS[1],
      { ...PAYMENT_HISTORY_COLUMNS[2], render: (record: PayoutRecord) => currencySign(country, record.commissionAmount) },
      {
        title: 'Payout Statement',
        field: '',
        cellStyle: { display: 'flex', justifyContent: 'flex-start' },
        render: (record: PayoutRecord) => (
          <IconButton
            size="small"
            disabled={downloading?.id === record.id && downloading?.category === 'pickslip'}
            onClick={(e) => { e.stopPropagation(); handleDownload(record, 'pickslip'); }}
            title="Download Payout Statement"
          >
            <FileDownloadOutlined fontSize="small" />
          </IconButton>
        ),
      },
      {
        title: 'Receipt',
        field: '',
        cellStyle: { display: 'flex', justifyContent: 'flex-start' },
        render: (record: PayoutRecord) => (
          <IconButton
            size="small"
            disabled={downloading?.id === record.id && downloading?.category === 'documents'}
            onClick={(e) => { e.stopPropagation(); handleDownload(record, 'documents'); }}
            title="Download Receipt"
          >
            <FileDownloadOutlined fontSize="small" />
          </IconButton>
        ),
      },
    ];
  }, [country, downloading, handleDownload, payoutRecords]);

  return (
    <>
      <SectionTitle>Payment History</SectionTitle>
      <GenericTableWrapper
        columns={columns}
        data={visibleRecords}
        customPagination={{
          currentPage,
          setCurrentPage,
          totalPages,
          itemsPerPage,
          setItemsPerPage,
          baseRoute: '',
          shouldRedirect: false,
        }}
      />
    </>
  );
};

export default PaymentHistory;
